<i18n locale="en">
{
  "unavailable": "Not available when sending to all locations.",
  "method-options": {
    "ai": "Make it with AI",
    "custom": "Make it yourself"
  },
  "generate": "Generate Email"
}
</i18n>

<template>
  <div class="template-sidebar flex-col gap-8">
    <template v-if="showLocationSelector">
      <div class="flex-col gap-2">
        <TitleBlock :title="templateConfig.heading" />
        <LocationSelector v-model="selectedLocation" />
      </div>
      <OwnRule type="dashed" />
    </template>

    <OwnTouchRadioGroup v-model="selectedMethod" :options="methodOptions" />
    <OwnRule type="dashed" />

    <div v-if="selectedMethod === 'custom'" class="flex-col gap-3">
      <OwnTouchRadioGroup
        v-model="selectedOption"
        :options="availableOptions"
      />

      <OwnCard
        v-for="option of unavailableOptions"
        :key="option.value"
        class="template-sidebar__unavailable-option"
      >
        <div class="flex-row align-center gap-3">
          <component :is="option.icon" size="24" />
          <div class="flex-col gap-1">
            <OwnType :text="option.label" variant="subtitle" />
            <OwnType :text="t('unavailable')" />
          </div>
        </div>
      </OwnCard>
    </div>

    <AiTemplateEditor
      v-else
      :location-id="selectedLocation"
      @custom="$emit('custom', $event)"
    />
  </div>
</template>

<script>
import { PhSparkle, PhPaintBrushHousehold } from '@phosphor-icons/vue'
import { isDefined } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import LocationSelector from '@/components/selectors/LocationSelector.vue'
import TitleBlock from '@/components/typography/TitleBlock.vue'
import { OwnCard } from '@/ui/OwnCard'
import { OwnRule } from '@/ui/OwnRule'
import { OwnTouchRadioGroup } from '@/ui/OwnTouchRadioGroup'
import { OwnType } from '@/ui/OwnType'
import { mapModel } from '@/utils/computed'

import AiTemplateEditor from './AiTemplateEditor.vue'

export default {
  name: 'TemplateSidebar',
  components: {
    AiTemplateEditor,
    LocationSelector,
    OwnCard,
    OwnRule,
    OwnTouchRadioGroup,
    OwnType,
    TitleBlock,
  },
  props: {
    modelValue: { type: String, default: undefined },
    overrides: { type: Object, default: () => {} },
    previewLoaded: { type: Boolean, required: true },
    templateConfig: { type: Object, required: true },
  },
  emits: ['custom', 'update:overrides', 'update:modelValue'],
  data() {
    return {
      methodOptions: ['ai', 'custom'].map((value) => ({
        icon: value === 'ai' ? PhSparkle : PhPaintBrushHousehold,
        label: this.t(`method-options.${value}`),
        value,
      })),
      selectedLocation: null,
      selectedMethod: 'ai',
    }
  },
  setup() {
    const { t } = useI18n()

    return { t }
  },
  computed: {
    ...mapModel('selectedOption'),
    ...mapGetters({
      locations: 'core/brand/active/locations',
    }),
    availableOptions() {
      const { templateConfig, selectedLocation } = this

      return templateConfig.options.filter((option) => {
        if (!isDefined(option.requiresLocation)) return true

        return option.requiresLocation && isDefined(selectedLocation)
      })
    },
    showLocationSelector() {
      const { locations } = this

      return locations?.length !== 1
    },
    unavailableOptions() {
      const { templateConfig, selectedLocation } = this

      return templateConfig.options.filter((option) => {
        if (!selectedLocation) return option.requiresLocation
        return false
      })
    },
  },
  watch: {
    previewLoaded(newPreviewLoaded) {
      if (newPreviewLoaded) {
        this.selectedOption = undefined
        this.$emit('custom', { blocks: [], resource: undefined })
      }
    },
    selectedLocation(newLocation) {
      this.$emit('update:overrides', { locationId: newLocation })

      const { selectedOption, availableOptions, unavailableOptions } = this

      // Moving from location to all locations
      if (
        !isDefined(newLocation) &&
        unavailableOptions.find((option) => option.value === selectedOption)
      ) {
        this.selectedOption = availableOptions[0].value
      }
    },
    selectedMethod(newMethod) {
      if (newMethod === 'ai') {
        this.selectedOption = undefined
        this.$emit('custom', { blocks: [], resource: undefined })
      } else {
        this.selectedOption = 'blank'
      }
    },
  },
  created() {
    const { locations, selectedLocation } = this

    if (locations?.length === 1 && !selectedLocation) {
      this.selectedLocation = locations[0].id
    }
  },
}
</script>

<style lang="scss" scoped>
.template-sidebar {
  &__unavailable-option {
    padding: 16px;
    color: var(--text-color-secondary);
    cursor: not-allowed;
  }
}
</style>
